import React, { useState, useEffect, useRef } from 'react';
import { track, getSlug } from '@/utils/tracker';
import { useNavigate } from 'react-router-dom';
import ChroLayout from '@/components/layouts/ChroLayout';
import { chroApi } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  Loader2,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  CheckCircle,
  Zap,
  Users,
  Heart,
  Bot,
  Clock,
  Accessibility,
  Download,
  Share2,
  Play,
  Copy,
  ExternalLink,
} from 'lucide-react';

const VALUE_DISCIPLINES = [
  {
    value: 'operational_excellence',
    label: 'Operational Excellence',
    description: 'Prioritises AI-Addressable and Fractional reallocation',
    metric: 'Cost vs ASBC optimal',
    icon: Zap,
  },
  {
    value: 'customer_intimacy',
    label: 'Customer Intimacy',
    description: 'Free Human-Strategic/Empathetic tasks from admin burden',
    metric: 'Hours redirected to customers',
    icon: Heart,
  },
  {
    value: 'product_leadership',
    label: 'Product Leadership',
    description: 'Remove AI-Addressable tasks from senior roles',
    metric: 'Senior hours freed for R&D',
    icon: Bot,
  },
  {
    value: 'growth_capacity',
    label: 'Growth Capacity',
    description: 'Fractional and Interim architecture for scale',
    metric: 'Headcount-to-revenue ratio',
    icon: Users,
  },
];

const WORKFLOW_STEPS = [
  { id: 1, label: 'Scope Selection', description: 'Select functions to molecularise' },
  { id: 2, label: 'Molecularisation', description: 'Deconstruct roles into tasks' },
  { id: 3, label: 'Value Discipline', description: 'Choose your strategic lens' },
  { id: 4, label: 'Blueprint Ready', description: 'Download or share your artefact' },
];

const formatCurrency = (value, currency = 'GBP') => {
  const symbols = { GBP: '£', USD: '$', AED: 'AED ', INR: '₹', MUR: 'Rs ' };
  const symbol = symbols[currency] || '£';
  if (value >= 1000000) return `${symbol}${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${symbol}${(value / 1000).toFixed(0)}K`;
  return `${symbol}${value?.toFixed(0) || 0}`;
};

const BlueprintRoom = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [signalData, setSignalData] = useState(null);
  const [selectedFunctions, setSelectedFunctions] = useState([]);
  const [blueprint, setBlueprint] = useState(null);
  const [selectedDiscipline, setSelectedDiscipline] = useState(null);
  const [molecularising, setMolecularising] = useState(false);
  const [blueprints, setBlueprints] = useState([]);
  const [expandedSections, setExpandedSections] = useState({});
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [sharingLink, setSharingLink] = useState(false);
  const [activatingStage, setActivatingStage] = useState(false);

  const _trackSlug = getSlug();
  const _trackEntry = useRef(Date.now());

  useEffect(() => {
    track(_trackSlug, 'screen_viewed', 'blueprint');
    return () => {
      const dur = Math.round((Date.now() - _trackEntry.current) / 1000);
      track(_trackSlug, 'screen_exited', 'blueprint', dur);
    };
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchData = async () => {
      if (localStorage.getItem("is_demo") === "true") { setLoading(false); return; }
      try {
        const [signalRes, blueprintsRes] = await Promise.all([
          chroApi.getSignalRoom(),
          chroApi.listBlueprints()
        ]);
        setSignalData(signalRes.data);
        setBlueprints(blueprintsRes.data);
        
        // Pre-select red and amber functions
        const preSelected = signalRes.data.functions
          ?.filter(f => f.signal_state === 'red' || f.signal_state === 'amber')
          .map(f => f.name) || [];
        setSelectedFunctions(preSelected);
      } catch (error) {
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleFunctionToggle = (funcName) => {
    setSelectedFunctions(prev => 
      prev.includes(funcName) 
        ? prev.filter(f => f !== funcName)
        : [...prev, funcName]
    );
  };

  const handleCreateBlueprint = async () => {
    if (selectedFunctions.length === 0) {
      toast.error('Select at least one function');
      return;
    }
    
    setMolecularising(true);
    try {
      const createRes = await chroApi.createBlueprint(selectedFunctions);
      const blueprintId = createRes.data.id;
      
      // Start molecularisation
      await chroApi.moleculariseBlueprint(blueprintId);
      
      // Poll for completion
      let attempts = 0;
      const maxAttempts = 30;
      
      const poll = async () => {
        const bpRes = await chroApi.getBlueprint(blueprintId);
        if (bpRes.data.status === 'ready') {
          setBlueprint(bpRes.data);
          setStep(3);
          setMolecularising(false);
        } else if (bpRes.data.status === 'failed') {
          throw new Error('Molecularisation failed');
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(poll, 2000);
        } else {
          throw new Error('Molecularisation timed out');
        }
      };
      
      setStep(2);
      poll();
    } catch (error) {
      toast.error(error.message || 'Failed to create blueprint');
      setMolecularising(false);
    }
  };

  const handleSetDiscipline = async () => {
    if (!selectedDiscipline || !blueprint) return;
    
    try {
      await chroApi.setValueDiscipline(blueprint.id, selectedDiscipline);
      const bpRes = await chroApi.getBlueprint(blueprint.id);
      setBlueprint(bpRes.data);
      setStep(4);
    } catch (error) {
      toast.error('Failed to set value discipline');
    }
  };

  const toggleSection = (sectionNumber) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionNumber]: !prev[sectionNumber]
    }));
  };

  const handleDownloadPdf = async () => {
    if (!blueprint) return;
    setDownloadingPdf(true);
    try {
      const response = await chroApi.downloadBlueprintPdf(blueprint.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `blueprint_${blueprint.id.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleShareLink = async () => {
    if (!blueprint) return;
    setSharingLink(true);
    try {
      const response = await chroApi.shareBlueprintLink(blueprint.id);
      const shareUrl = response.data.url;
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Share link copied to clipboard', {
        description: `Expires: ${new Date(response.data.expires_at).toLocaleDateString()}`
      });
    } catch (error) {
      toast.error('Failed to create share link');
    } finally {
      setSharingLink(false);
    }
  };

  const handleActivateStage = async () => {
    if (!blueprint) return;
    setActivatingStage(true);
    try {
      const response = await chroApi.activateStage(blueprint.id);
      toast.success(response.data.message);
      navigate('/stage');
    } catch (error) {
      toast.error('Failed to activate Stage');
    } finally {
      setActivatingStage(false);
    }
  };

  const renderSectionContent = (section) => {
    const content = section.content || {};
    const sectionNum = section.section_number;

    if (sectionNum === 1) {
      return (
        <div className="space-y-2 p-4 bg-background/30 rounded-lg">
          {content.headline && <p className="font-medium text-sm">{content.headline}</p>}
          {content.key_points?.map((point, i) => (
            <p key={i} className="text-sm text-muted-foreground">• {point}</p>
          ))}
        </div>
      );
    }

    if (sectionNum === 2) {
      return (
        <div className="p-4 bg-background/30 rounded-lg space-y-2">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <span className="text-muted-foreground">Functions Analysed:</span>
            <span>{content.functions_analyzed?.join(', ') || 'N/A'}</span>
            <span className="text-muted-foreground">Total Headcount:</span>
            <span>{content.total_headcount || 0}</span>
            <span className="text-muted-foreground">Current Cost:</span>
            <span>{formatCurrency(content.current_cost || 0, currency)}</span>
          </div>
          {content.signal_breakdown && (
            <div className="flex gap-4 mt-2 text-sm">
              <Badge variant="outline" className="border-green-500 text-green-500">Green: {content.signal_breakdown.green || 0}</Badge>
              <Badge variant="outline" className="border-yellow-500 text-yellow-500">Amber: {content.signal_breakdown.amber || 0}</Badge>
              <Badge variant="outline" className="border-red-500 text-red-500">Red: {content.signal_breakdown.red || 0}</Badge>
            </div>
          )}
        </div>
      );
    }

    if (sectionNum === 3) {
      const wb = content.workforce_type_breakdown || {};
      const total = (wb.human_strategic || 0) + (wb.human_empathetic || 0) + (wb.ai_addressable || 0) + (wb.fractional || 0) + (wb.interim || 0) + (wb.inclusion_pathway || 0);
      const pct = (val) => total > 0 ? `${Math.round((val / total) * 100)}%` : '0%';
      return (
        <div className="p-4 bg-background/30 rounded-lg space-y-2 text-sm">
          <div className="grid grid-cols-3 gap-2">
            <span className="text-muted-foreground">Human · Strategic:</span><span>{wb.human_strategic || 0}</span><span className="text-muted-foreground">{pct(wb.human_strategic || 0)}</span>
            <span className="text-muted-foreground">Human · Empathetic:</span><span>{wb.human_empathetic || 0}</span><span className="text-muted-foreground">{pct(wb.human_empathetic || 0)}</span>
            <span className="text-muted-foreground">AI-Addressable:</span><span>{wb.ai_addressable || 0}</span><span className="text-muted-foreground">{pct(wb.ai_addressable || 0)}</span>
            <span className="text-muted-foreground">Fractional:</span><span>{wb.fractional || 0}</span><span className="text-muted-foreground">{pct(wb.fractional || 0)}</span>
            <span className="text-muted-foreground">Interim:</span><span>{wb.interim || 0}</span><span className="text-muted-foreground">{pct(wb.interim || 0)}</span>
            <span className="text-muted-foreground">Inclusion Pathway:</span><span>{wb.inclusion_pathway || 0}</span><span className="text-muted-foreground">{pct(wb.inclusion_pathway || 0)}</span>
          </div>
          {content.precision_gap !== undefined && (
            <p className="font-medium mt-2">Precision Gap: {formatCurrency(content.precision_gap, currency)}</p>
          )}
        </div>
      );
    }

    if (sectionNum === 4) {
      const disc = blueprint?.value_discipline?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      return (
        <div className="p-4 bg-background/30 rounded-lg text-sm">
          <p><span className="text-muted-foreground">Selected Discipline:</span> <span className="font-medium">{disc || 'Not selected'}</span></p>
        </div>
      );
    }

    if (sectionNum === 5) {
      return (
        <div className="p-4 bg-background/30 rounded-lg space-y-1 text-sm">
          <p>Inclusion Pathways: {content.inclusion_pathways_identified || 0}</p>
          <p>Estimated Headcount Impact: {content.estimated_headcount || 0}</p>
          <p>D&I Improvement: {content.di_improvement_potential || 'N/A'}</p>
        </div>
      );
    }

    if (sectionNum === 6) {
      return (
        <div className="p-4 bg-background/30 rounded-lg text-sm">
          <div className="flex gap-4">
            <Badge variant="outline" className="border-green-500 text-green-500">Keep: {content.keep || 0}</Badge>
            <Badge variant="outline" className="border-yellow-500 text-yellow-500">Redesign: {content.redesign || 0}</Badge>
            <Badge variant="outline" className="border-red-500 text-red-500">Transform: {content.transform || 0}</Badge>
          </div>
        </div>
      );
    }

    if (sectionNum === 7) {
      return (
        <div className="p-4 bg-background/30 rounded-lg space-y-1 text-sm">
          <p>Human Roles for Stage: {content.human_roles_for_stage || 0}</p>
          <p>AI-Addressable task clusters identified: {content.ai_task_clusters_identified || 0}</p>
          <p>Tools shortlisted for Showcase: up to {content.tools_shortlisted_for_showcase || 0}</p>
          <p>Estimated Timeline: {content.timeline_weeks || 0} weeks</p>
        </div>
      );
    }

    if (sectionNum === 8) {
      return (
        <div className="p-4 bg-background/30 rounded-lg space-y-1 text-sm">
          <div className="grid grid-cols-2 gap-2">
            <span className="text-muted-foreground">Investment Required:</span>
            <span>{formatCurrency(content.investment_required || 0, currency)}</span>
            <span className="text-muted-foreground">Expected Improvement:</span>
            <span>{formatCurrency(content.expected_improvement || 0, currency)}</span>
            <span className="text-muted-foreground">Payback Period:</span>
            <span>{content.payback_months || 0} months</span>
            <span className="text-muted-foreground">3-Year Projection:</span>
            <span>{formatCurrency(content.three_year_projection || 0, currency)}</span>
          </div>
        </div>
      );
    }

    return <div className="p-4 bg-background/30 rounded-lg text-sm text-muted-foreground">No additional details</div>;
  };

  if (loading) {
    return (
      <ChroLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </ChroLayout>
    );
  }

  const currency = signalData?.currency || 'GBP';

  return (
    <ChroLayout orgName={signalData?.org_name}>
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {WORKFLOW_STEPS.map((ws, index) => (
              <div key={ws.id} className="flex items-center">
                <div className={`flex items-center gap-2 ${step >= ws.id ? 'text-primary' : 'text-muted-foreground'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step > ws.id ? 'bg-primary text-primary-foreground' :
                    step === ws.id ? 'bg-primary/20 text-primary border-2 border-primary' :
                    'bg-muted text-muted-foreground'
                  }`}>
                    {step > ws.id ? <CheckCircle className="h-4 w-4" /> : ws.id}
                  </div>
                  <div className="hidden md:block">
                    <p className="text-sm font-medium">{ws.label}</p>
                    <p className="text-xs text-muted-foreground">{ws.description}</p>
                  </div>
                </div>
                {index < WORKFLOW_STEPS.length - 1 && (
                  <div className={`w-12 md:w-24 h-0.5 mx-2 ${step > ws.id ? 'bg-primary' : 'bg-border'}`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step 1: Scope Selection */}
        {step === 1 && (
          <div className="space-y-6 animate-fade-in">
            <div>
              <h2 className="text-2xl font-bold">Select Functions to Molecularise</h2>
              <p className="text-muted-foreground mt-1">
                Choose which functions to analyse. Red and amber signals are pre-selected.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {signalData?.functions?.map((func) => (
                <Card 
                  key={func.name}
                  className={`card-interactive cursor-pointer ${
                    selectedFunctions.includes(func.name) ? 'border-primary ring-2 ring-primary/20' : ''
                  }`}
                  onClick={() => handleFunctionToggle(func.name)}
                  data-testid={`function-select-${func.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <Checkbox 
                        checked={selectedFunctions.includes(func.name)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h3 className="font-medium">{func.name}</h3>
                          <div className={`signal-dot signal-dot-${func.signal_state}`} />
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {func.headcount} FTE • {formatCurrency(func.gap, currency)} gap
                        </p>
                        <div className="h-1.5 bg-background rounded-full mt-2 overflow-hidden">
                          <div 
                            className={`h-full rounded-full ${
                              func.signal_state === 'red' ? 'bg-destructive' :
                              func.signal_state === 'amber' ? 'bg-warning' : 'bg-accent'
                            }`}
                            style={{ width: `${Math.min(func.gap_percentage || 0, 100)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="flex justify-end">
              <Button 
                onClick={handleCreateBlueprint}
                disabled={selectedFunctions.length === 0}
                data-testid="start-molecularisation-btn"
              >
                Start Molecularisation
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Molecularising */}
        {step === 2 && (
          <div className="flex flex-col items-center justify-center py-12 animate-fade-in">
            <div className="relative w-24 h-24 mb-6">
              <Loader2 className="h-24 w-24 animate-spin text-primary" />
            </div>
            <h2 className="text-xl font-bold mb-2">Molecularising Functions</h2>
            <p className="text-muted-foreground text-center max-w-md">
              Deconstructing {selectedFunctions.length} function(s) into tasks using the OCF task framework...
            </p>
            <div className="flex flex-wrap gap-2 mt-4 justify-center">
              {selectedFunctions.map((func) => (
                <Badge key={func} variant="outline">{func}</Badge>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Value Discipline */}
        {step === 3 && (
          <div className="space-y-6 animate-fade-in">
            <div>
              <h2 className="text-2xl font-bold">Choose Your Value Discipline</h2>
              <p className="text-muted-foreground mt-1">
                Select the strategic lens for your transformation blueprint.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {VALUE_DISCIPLINES.map((discipline) => (
                <Card 
                  key={discipline.value}
                  className={`card-interactive cursor-pointer ${
                    selectedDiscipline === discipline.value ? 'border-primary ring-2 ring-primary/20' : ''
                  }`}
                  onClick={() => setSelectedDiscipline(discipline.value)}
                  data-testid={`discipline-${discipline.value}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-lg bg-primary/10">
                        <discipline.icon className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{discipline.label}</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          {discipline.description}
                        </p>
                        <Badge variant="outline" className="mt-3">
                          Metric: {discipline.metric}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)}>
                <ChevronLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <Button 
                onClick={handleSetDiscipline}
                disabled={!selectedDiscipline}
                data-testid="generate-blueprint-btn"
              >
                Generate Blueprint
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Blueprint Ready */}
        {step === 4 && blueprint && (
          <div className="space-y-6 animate-fade-in">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">Your Blueprint is Ready</h2>
                <p className="text-muted-foreground mt-1">
                  Review the 8-section board artefact and take action.
                </p>
              </div>
              <Badge variant="outline" className="text-primary border-primary">
                {blueprint.roles?.length || 0} roles analysed
              </Badge>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="glass-light">
                <CardContent className="p-6 text-center">
                  <p className="text-sm text-muted-foreground">Current Cost</p>
                  <p className="text-2xl font-bold mt-1">
                    {formatCurrency(blueprint.total_current_cost, currency)}
                  </p>
                </CardContent>
              </Card>
              <Card className="glass-light">
                <CardContent className="p-6 text-center">
                  <p className="text-sm text-muted-foreground">ASBC Optimal</p>
                  <p className="text-2xl font-bold mt-1 text-primary">
                    {formatCurrency(blueprint.total_asbc_optimal, currency)}
                  </p>
                </CardContent>
              </Card>
              <Card className="glass-light glow-primary">
                <CardContent className="p-6 text-center">
                  <p className="text-sm text-muted-foreground">Transformation Potential</p>
                  <p className="text-2xl font-bold mt-1 gradient-text">
                    {formatCurrency(blueprint.total_gap, currency)}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Blueprint Sections */}
            <Card className="glass-light">
              <CardHeader>
                <CardTitle className="text-base">Blueprint Sections</CardTitle>
                <CardDescription>Click each section to expand details</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {blueprint.sections?.map((section) => (
                    <div key={section.section_number}>
                      <button 
                        className="w-full flex items-center justify-between p-3 rounded-lg bg-background/50 hover:bg-background transition-colors text-left"
                        onClick={() => toggleSection(section.section_number)}
                        data-testid={`blueprint-section-${section.section_number}`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-primary/20 text-primary text-sm flex items-center justify-center font-medium shrink-0">
                            {section.section_number}
                          </span>
                          <span className="font-medium">{section.title}</span>
                        </div>
                        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${
                          expandedSections[section.section_number] ? 'rotate-180' : ''
                        }`} />
                      </button>
                      {expandedSections[section.section_number] && (
                        <div className="mt-2 ml-9 animate-fade-in">
                          {renderSectionContent(section)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <Card className="glass-heavy">
              <CardContent className="p-6">
                <div className="flex flex-wrap gap-3">
                  <Button 
                    variant="outline" 
                    onClick={handleDownloadPdf}
                    disabled={downloadingPdf}
                    data-testid="download-pdf-btn"
                  >
                    {downloadingPdf ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                    {downloadingPdf ? 'Generating...' : 'Download PDF'}
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={handleShareLink}
                    disabled={sharingLink}
                    data-testid="share-link-btn"
                  >
                    {sharingLink ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Share2 className="h-4 w-4 mr-2" />}
                    {sharingLink ? 'Creating...' : 'Share Link'}
                  </Button>
                  <Button 
                    onClick={handleActivateStage}
                    disabled={activatingStage}
                    data-testid="activate-stage-btn"
                  >
                    {activatingStage ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Users className="h-4 w-4 mr-2" />}
                    {activatingStage ? 'Activating...' : 'Activate Stage'}
                  </Button>
                  <Button 
                    onClick={() => navigate(`/showcase/${blueprint.id}`)}
                    data-testid="activate-showcase-btn"
                  >
                    <Bot className="h-4 w-4 mr-2" />
                    Activate Showcase
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Previous Blueprints */}
        {blueprints.length > 0 && step === 1 && (
          <Card className="mt-8 glass-light">
            <CardHeader>
              <CardTitle className="text-base">Previous Blueprints</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {blueprints.slice(0, 5).map((bp) => (
                  <div 
                    key={bp.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-background/50"
                  >
                    <div>
                      <p className="font-medium">{bp.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {bp.selected_functions?.join(', ')}
                      </p>
                    </div>
                    <Badge variant={bp.status === 'ready' ? 'default' : 'outline'}>
                      {bp.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </ChroLayout>
  );
};

export default BlueprintRoom;
